import type { ConsentConfig, ConsentSource, ConsentState } from './types';

export const DEFAULT_TTL_DAYS = 180;
export const DEFAULT_COOKIE_NAME = 'privacy_consent';
export const DECISION_MARKER_SUFFIX = '_decision';
const DECISION_MARKER_TTL_DAYS = 400;

const encodeState = (state: ConsentState): string => encodeURIComponent(JSON.stringify(state));

const decodeState = (value: string): ConsentState | null => {
  try {
    return JSON.parse(decodeURIComponent(value)) as ConsentState;
  } catch {
    return null;
  }
};

export interface ConsentCookieReadResult {
  state: ConsentState | null;
  status: 'missing' | 'valid' | 'invalid';
}

const readCookieValue = (cookieString: string, name: string): string | undefined => {
  const prefix = `${name}=`;
  return cookieString
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix))
    ?.slice(prefix.length);
};

export const getGpcState = (): boolean => typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;

export const getEffectiveMode = (config: ConsentConfig): Exclude<ConsentConfig['mode'], 'regional'> => {
  if (config.mode !== 'regional') return config.mode;
  const profile = config.resolvedProfile ?? 'unknown';
  return config.regionalProfiles?.[profile] ?? 'global-strict';
};

export const defaultPreferences = (config: ConsentConfig, gpc = getGpcState()): Record<string, boolean> => {
  const mode = getEffectiveMode(config);
  return Object.fromEntries(
    config.categories.map((category) => [
      category.id,
      category.required || (mode === 'notice-with-optout' && !(gpc && category.gpcOptOut)),
    ]),
  );
};

export const applyPolicyConstraints = (
  config: ConsentConfig,
  preferences: Record<string, boolean>,
  gpc = getGpcState(),
): Record<string, boolean> => {
  return Object.fromEntries(
    config.categories.map((category) => {
      const requested = Boolean(preferences[category.id]);
      const allowed = category.required || requested;
      return [category.id, category.required || (!(gpc && category.gpcOptOut) && allowed)];
    }),
  );
};

export const createState = (
  config: ConsentConfig,
  preferences: Record<string, boolean>,
  source: ConsentSource,
  gpc = getGpcState(),
  now = new Date(),
): ConsentState => {
  const ttlDays = config.consentTtlDays ?? DEFAULT_TTL_DAYS;
  const expiresAt = new Date(now.getTime() + ttlDays * 24 * 60 * 60 * 1000);

  return {
    schemaVersion: 1,
    siteId: config.siteId,
    policyVersion: config.policyVersion,
    consentId: globalThis.crypto?.randomUUID?.() ?? `${now.getTime()}-${Math.random().toString(36).slice(2)}`,
    categories: applyPolicyConstraints(config, preferences, gpc),
    source,
    locale: config.locale ?? 'ja',
    profile: config.resolvedProfile ?? 'unknown',
    gpc,
    decidedAt: now.toISOString(),
    expiresAt: expiresAt.toISOString(),
  };
};

const isStateValid = (config: ConsentConfig, state: ConsentState | null, now = new Date()): state is ConsentState => {
  if (!state || state.schemaVersion !== 1) return false;
  if (state.siteId !== config.siteId || state.policyVersion !== config.policyVersion) return false;
  if (!state.expiresAt || Number.isNaN(Date.parse(state.expiresAt)) || Date.parse(state.expiresAt) <= now.getTime()) return false;
  if (!state.categories || typeof state.categories !== 'object') return false;
  return config.categories.every((category) => typeof state.categories[category.id] === 'boolean');
};

export const readConsentCookieResult = (config: ConsentConfig, cookieString = document.cookie): ConsentCookieReadResult => {
  const name = config.cookieName ?? DEFAULT_COOKIE_NAME;
  const value = readCookieValue(cookieString, name);

  if (!value) {
    const marker = readCookieValue(cookieString, `${name}${DECISION_MARKER_SUFFIX}`);
    return { state: null, status: marker ? 'invalid' : 'missing' };
  }
  const state = decodeState(value);
  if (!isStateValid(config, state)) return { state: null, status: 'invalid' };
  return { state, status: 'valid' };
};

export const readConsentCookie = (config: ConsentConfig, cookieString = document.cookie): ConsentState | null => (
  readConsentCookieResult(config, cookieString).state
);

export const writeConsentCookie = (config: ConsentConfig, state: ConsentState): void => {
  const name = config.cookieName ?? DEFAULT_COOKIE_NAME;
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeState(state)}; Expires=${new Date(state.expiresAt).toUTCString()}; Path=/; SameSite=Lax${secure}`;
  const markerExpiresAt = new Date(Date.now() + DECISION_MARKER_TTL_DAYS * 24 * 60 * 60 * 1000);
  document.cookie = `${name}${DECISION_MARKER_SUFFIX}=1; Expires=${markerExpiresAt.toUTCString()}; Path=/; SameSite=Lax${secure}`;
};

export const deleteCookie = (name: string, path = '/', domain?: string): void => {
  const domainPart = domain ? `; Domain=${domain}` : '';
  document.cookie = `${name}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0; Path=${path}; SameSite=Lax${domainPart}`;
};

export const validateConfig = (config: ConsentConfig): void => {
  if (!config.siteId || !config.policyVersion || !config.policyUrl) {
    throw new Error('siteId、policyVersion、policyUrlは必須です');
  }
  if (!Array.isArray(config.categories) || config.categories.length === 0) {
    throw new Error('1件以上のカテゴリが必要です');
  }
  const ids = new Set<string>();
  for (const category of config.categories) {
    if (!category.id || ids.has(category.id)) throw new Error(`カテゴリIDが不正です: ${category.id}`);
    if (category.required && category.gpcOptOut) {
      throw new Error(`必須カテゴリ ${category.id} にgpcOptOutは指定できません`);
    }
    ids.add(category.id);
  }
  if (!config.categories.some((category) => category.required)) {
    throw new Error('必須カテゴリが必要です');
  }
  const vendorIds = new Set<string>();
  for (const vendor of config.vendors) {
    if (!vendor.id || vendorIds.has(vendor.id)) throw new Error(`ベンダーIDが不正です: ${vendor.id}`);
    vendorIds.add(vendor.id);
    if (!ids.has(vendor.categoryId)) throw new Error(`ベンダー ${vendor.id} のカテゴリが存在しません`);
    if (vendor.onDisable !== undefined && typeof vendor.onDisable !== 'function') {
      throw new Error(`ベンダー ${vendor.id} のonDisableは関数で指定してください`);
    }
  }
  const effectiveMode = getEffectiveMode(config);
  if (effectiveMode === 'notice-only') {
    const optionalIds = new Set(config.categories.filter((category) => !category.required).map((category) => category.id));
    if (config.vendors.some((vendor) => optionalIds.has(vendor.categoryId))) {
      throw new Error('notice-onlyでは任意カテゴリのサービスを事前実行できません');
    }
  }
};
